/**
 * TipTap configuration of the one rich-text editor (DESIGN.md §5 "Rich content", T11):
 * the e-mail-safe schema plus our own extensions (indent, signature block, keyboard hooks).
 *
 * The schema is the first sanitizer: pasted or loaded HTML is parsed into these nodes and marks,
 * everything else (classes, scripts, unknown tags, most styles) is dropped. The server sanitizes
 * again before sending (services/platform-api, modules/mailbox/html.ts).
 */
import { Extension, Node, mergeAttributes, type CommandProps, type Extensions } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { Color, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import StarterKit from '@tiptap/starter-kit';
import { tableExtensions } from './table';

/** Indent step in px (Outlook/Word use 0.5 in ≈ 48 px; 40 px matches browsers' list indent). */
export const INDENT_STEP_PX = 40;
export const MAX_INDENT = 8;

/** Attribute that marks the inserted signature (signatures research §5b). */
export const SIGNATURE_ATTRIBUTE = 'data-gx-sig';

const INDENTABLE = ['paragraph', 'heading'] as const;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      /** Indent: nests a list item, otherwise adds left margin to the selected paragraphs. */
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
    signature: {
      /**
       * Inserts the signature block, or replaces the existing one (only one per message).
       * `html` is the rendered signature, `id` the marker value `<profile>:<version>`.
       */
      setSignature: (signature: { id: string; html: string }) => ReturnType;
      removeSignature: () => ReturnType;
    };
  }
}

/** Changes the indent level of the selected paragraphs/headings; false if nothing changed. */
function shiftIndent({ tr, state }: CommandProps, delta: number): boolean {
  let changed = false;
  state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
    if (!(INDENTABLE as readonly string[]).includes(node.type.name)) return true;
    const level = Math.max(0, Math.min(MAX_INDENT, Number(node.attrs.indent ?? 0) + delta));
    if (level !== node.attrs.indent) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: level });
      changed = true;
    }
    return false;
  });
  return changed;
}

/** Left margin in steps of INDENT_STEP_PX on paragraphs and headings (`margin-left` in the HTML). */
export const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes() {
    return [
      {
        types: [...INDENTABLE],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const px = Number.parseFloat(element.style.marginLeft);
              return Number.isFinite(px) && px > 0 ? Math.min(MAX_INDENT, Math.round(px / INDENT_STEP_PX)) : 0;
            },
            renderHTML: (attributes) => {
              const level = Number(attributes.indent ?? 0);
              return level > 0 ? { style: `margin-left: ${level * INDENT_STEP_PX}px` } : {};
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent: () => (props) => props.commands.sinkListItem('listItem') || shiftIndent(props, 1),
      outdent: () => (props) => props.commands.liftListItem('listItem') || shiftIndent(props, -1),
    };
  },

  addKeyboardShortcuts() {
    // Word/Outlook: Ctrl+M / Ctrl+Shift+M. Tab only inside lists (keeps Tab for focus moves).
    return {
      'Mod-m': () => this.editor.commands.indent(),
      'Mod-Shift-m': () => this.editor.commands.outdent(),
    };
  },
});

/**
 * The signature block: one node holding the rendered signature, marked with
 * `data-gx-sig="<profile>:<version>"` so the transport fallback (milter) does not stamp a second
 * signature and switching signatures replaces exactly this block. Inserting it is the job of the
 * signature service integration (P6); the editor only provides the node and the commands.
 */
export const Signature = Node.create({
  name: 'signature',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute(SIGNATURE_ATTRIBUTE),
        renderHTML: (attributes) => ({ [SIGNATURE_ATTRIBUTE]: attributes.id as string | null }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[${SIGNATURE_ATTRIBUTE}]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    const findSignature = (doc: ProseMirrorNode): { from: number; to: number } | undefined => {
      let found: { from: number; to: number } | undefined;
      doc.descendants((node, pos) => {
        if (found) return false;
        if (node.type.name === this.name) found = { from: pos, to: pos + node.nodeSize };
        return !found;
      });
      return found;
    };
    return {
      setSignature:
        ({ id, html }) =>
        ({ state, commands }) => {
          const block = `<div ${SIGNATURE_ATTRIBUTE}="${id.replace(/[&"<>]/g, '')}">${html}</div>`;
          const existing = findSignature(state.doc);
          // Signature hook position of a reply/forward: above the quoted history (Outlook), else at the end.
          return commands.insertContentAt(existing ?? findQuote(state.doc)?.from ?? state.doc.content.size, block);
        },
      removeSignature:
        () =>
        ({ state, commands }) => {
          const existing = findSignature(state.doc);
          return existing ? commands.deleteRange(existing) : false;
        },
    };
  },
});

/** Attribute that marks the quoted history of a reply/forward (like Outlook's `divRplyFwdMsg`). */
export const QUOTE_ATTRIBUTE = 'data-gx-quote';
/** Separator line above the quoted history (Outlook's style; a mail content color, not a theme token). */
export const QUOTE_STYLE = 'border-top: 1px solid #e1e1e1; padding-top: 3pt';

/** Position of the quoted history block, if the document has one. */
function findQuote(doc: ProseMirrorNode): { from: number; to: number } | undefined {
  let found: { from: number; to: number } | undefined;
  doc.descendants((node, pos) => {
    if (found) return false;
    if (node.type.name === 'mailQuote') found = { from: pos, to: pos + node.nodeSize };
    return !found;
  });
  return found;
}

/**
 * The quoted history of a reply/forward (DESIGN.md T11): one editable block with the separator
 * line, the "From / Sent / To / Cc / Subject" header and the original. Marked with
 * `data-gx-quote`, so the signature goes above it and later steps can tell new text from history.
 */
export const MailQuote = Node.create({
  name: 'mailQuote',
  group: 'block',
  content: 'block+',
  defining: true,

  parseHTML() {
    return [{ tag: `div[${QUOTE_ATTRIBUTE}]` }];
  },

  renderHTML() {
    return ['div', { [QUOTE_ATTRIBUTE]: '', style: QUOTE_STYLE }, 0];
  },
});

/**
 * Keys that belong to the surrounding UI: Ctrl+K opens the link dialog (outside the editor), and
 * Ctrl+Enter is "Send" in the composer (Outlook keymap), not a line break as in TipTap's default.
 */
export const AppShortcuts = Extension.create<{ onLink: () => void }>({
  name: 'appShortcuts',
  priority: 1000,
  addOptions: () => ({ onLink: () => undefined }),
  addKeyboardShortcuts() {
    return {
      'Mod-k': () => {
        this.options.onLink();
        return true;
      },
      // Consumed here so no line break is inserted; the event still reaches the page's Send shortcut.
      'Mod-Enter': () => true,
    };
  },
});

/** Horizontal line in mail content colors (a bare `<hr>` looks different in every client). */
export const HORIZONTAL_RULE_STYLE = 'border: 0; border-top: 1px solid #c8c8c8; margin: 8px 0';

export const PICTURE_SIZES = [
  { id: 'small', width: 120 },
  { id: 'medium', width: 240 },
  { id: 'original', width: null },
] as const;
export type PictureSizeId = (typeof PICTURE_SIZES)[number]['id'];
export const PICTURE_PLACEMENTS = ['inline', 'left'] as const;
export type PicturePlacement = (typeof PICTURE_PLACEMENTS)[number];
const LEFT_FLOAT_STYLE = 'float: left; margin: 0 8px 4px 0';

/** Pictures with a width in px (`width` attribute, which Outlook honors) and optional left placement (float). */
const EditorImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const width = Number.parseFloat(element.getAttribute('width') ?? '');
          return Number.isFinite(width) && width > 0 ? Math.round(width) : null;
        },
      },
      height: { default: null, renderHTML: () => ({}) },
      placement: {
        default: 'inline' as PicturePlacement,
        parseHTML: (element: HTMLElement) => (/float\s*:\s*left/i.test(element.getAttribute('style') ?? '') ? 'left' : 'inline'),
        renderHTML: (attributes: Record<string, unknown>) => (attributes.placement === 'left' ? { style: LEFT_FLOAT_STYLE } : {}),
      },
    };
  },
});

export interface EditorExtensionOptions {
  placeholder?: string | undefined;
  onLinkShortcut: () => void;
}

export function editorExtensions({ placeholder, onLinkShortcut }: EditorExtensionOptions): Extensions {
  return [
    StarterKit.configure({
      // Not offered in mail: headings come in via paste only as paragraphs, code is not mail-safe.
      code: false,
      codeBlock: false,
      heading: { levels: [1, 2, 3] },
      horizontalRule: { HTMLAttributes: { style: HORIZONTAL_RULE_STYLE } },
      link: {
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        defaultProtocol: 'https',
        protocols: ['mailto', 'tel'],
        HTMLAttributes: { rel: null, target: null, class: null },
        isAllowedUri: (url) => isSafeLink(url),
      },
    }),
    TextStyle,
    Color,
    FontSize,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['paragraph', 'heading'] }),
    EditorImage.configure({ inline: true, allowBase64: true }),
    ...tableExtensions(),
    Indent,
    Signature,
    MailQuote,
    AppShortcuts.configure({ onLink: onLinkShortcut }),
    Placeholder.configure({ placeholder: placeholder ?? '' }),
  ];
}

/** Links the editor accepts: http(s), mailto and tel (same set as the server sanitizer). */
export function isSafeLink(url: string): boolean {
  return /^(https?:\/\/|mailto:|tel:)/i.test(url.trim());
}

/** Normalizes user input for a link: adds https:// to bare host names, keeps mailto:/tel:. */
export function normalizeLink(input: string): string | undefined {
  const value = input.trim();
  if (value === '') return undefined;
  if (isSafeLink(value)) return value;
  if (/^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(value)) return `mailto:${value}`;
  if (/^[\w-]+(\.[\w-]+)+([/:?#].*)?$/.test(value)) return `https://${value}`;
  return undefined;
}
